import { __decorate } from "tslib";
import React, { isValidElement } from 'react';
import { action, computed, isArrayLike, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import { BigNumber } from 'bignumber.js';
import { math } from 'choerodon-ui/dataset';
import { FormatNumberFuncOptions } from 'choerodon-ui/dataset/data-set/Field';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { TextField } from '../text-field/TextField';
import autobind from '../_util/autobind';
import keepRunning from '../_util/keepRunning';
import Icon from '../icon';
import { getNearStepValues, parseNumber } from './utils';
import isEmpty from '../_util/isEmpty';
import { $l } from '../locale-context';
import { FieldType } from '../data-set/enum';
import defaultFormatNumber from '../formatter/formatNumber';
import localeContext from '../locale-context/LocaleContext';
import { getNumberFormatOptions, getNumberFormatter } from '../field/utils';
function getCurrentValidValue(value, options) {
    const valueBig = new BigNumber(value.replace(/\.$/, ''));
    return math.isValidBigNumber(valueBig) ? options.strict ? valueBig : math.fix(valueBig) : 0;
}
function getStepUpGenerator() {
    return this.stepGenerator(true);
}
function getStepDownGenerator() {
    return this.stepGenerator(false);
}
function run(value) {
    const { element } = this;
    if (element) {
        element.value = String(value);
    }
}
function enabled() {
    return Boolean(this.getProp('step'));
}
const stepUpKeyboardProps = {
    getStepGenerator: getStepUpGenerator,
    run,
    validator(target) {
        return target === this.element;
    },
    enabled,
};
const stepDownKeyboardProps = {
    getStepGenerator: getStepDownGenerator,
    run,
    validator(target) {
        return target === this.element;
    },
    enabled,
};
const stepUpMouseProps = {
    getStepGenerator: getStepUpGenerator,
    run,
    validator(target) {
        return target === this.plusElement;
    },
};
const stepDownMouseProps = {
    getStepGenerator: getStepDownGenerator,
    run,
    validator(target) {
        return target === this.minusElement;
    },
};
export { FormatNumberFuncOptions };
export class NumberField extends TextField {
    get lang() {
        const { lang } = this.observableProps;
        if (lang) {
            return lang;
        }
        const { dataSet } = this;
        if (dataSet && dataSet.lang) {
            return dataSet.lang;
        }
        const { numberFormatLanguage, locale } = localeContext;
        if (numberFormatLanguage) {
            return numberFormatLanguage;
        }
        return locale.lang;
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('NumberField', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
        };
    }
    get strict() {
        const { field } = this;
        return field ? field.get('type', this.record) === FieldType.bigNumber : false;
    }
    get allowDecimal() {
        const { min, nonStrictStep } = this;
        const precision = this.getProp('precision');
        if (precision === 0) {
            return false;
        }
        // 非严格步距下允许输入小数
        if (nonStrictStep) {
            return true;
        }
        const step = this.getProp('step');
        return !step || !math.eq(math.mod(step, 1), 0) || (!!min && !math.eq(math.mod(min, 1), 0));
    }
    get nonStrictStep() {
        const nonStrictStep = this.getProp('nonStrictStep');
        if (nonStrictStep !== undefined) {
            return nonStrictStep;
        }
        const numberFieldNonStrictStep = this.getContextConfig('numberFieldNonStrictStep');
        if (numberFieldNonStrictStep !== undefined) {
            return numberFieldNonStrictStep;
        }
        return false;
    }
    get allowNegative() {
        const { min } = this;
        return isNil(min) || min < 0;
    }
    get min() {
        return this.getLimit('min');
    }
    get max() {
        return this.getLimit('max');
    }
    get value() {
        const { value } = this.observableProps;
        if (isArrayLike(value)) {
            return value;
        }
        const { range } = this;
        if (isArrayLike(range)) {
            if (isPlainObject(value)) {
                const [start, end] = range;
                return {
                    [start]: value[start],
                    [end]: value[end],
                };
            }
        }
        return value;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    get clearButton() {
        const step = this.getProp('step');
        if (step) {
            return false;
        }
        return super.clearButton;
    }
    get keyboard() {
        if ('keyboard' in this.props) {
            return this.props.keyboard;
        }
        return this.getContextConfig('numberFieldKeyboard') !== false;
    }
    savePlusRef(ref) {
        this.plusElement = ref;
    }
    saveMinusRef(ref) {
        this.minusElement = ref;
    }
    isLowerRange(value1, value2) {
        return math.lt(value1, value2);
    }
    getFieldType() {
        return FieldType.number;
    }
    getLimit(type) {
        const { record } = this;
        const limit = this.getProp(type);
        if (record && isString(limit)) {
            const num = record.get(limit);
            if (num !== undefined) {
                return num;
            }
        }
        return limit;
    }
    getValidatorProp(key) {
        if (['maxLength', 'minLength'].includes(key)) {
            return;
        }
        switch (key) {
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            case 'nonStrictStep':
                return this.nonStrictStep;
            default:
                return super.getValidatorProp(key);
        }
    }
    getInnerSpanButton() {
        const { prefixCls, range, clearButton } = this;
        const { longPressPlus } = this.props;
        const step = this.getProp('step');
        if (step && !range && !this.readOnly && !this.disabled) {
            const plusIconProps = {
                ref: this.savePlusRef,
                key: 'plus',
                className: `${prefixCls}-plus`,
                onMouseDown: longPressPlus ? this.handlePlus : this.handleOncePlus,
                onTouchStart: this.handleOncePlus,
            };
            const minIconProps = {
                ref: this.saveMinusRef,
                key: 'minus',
                className: `${prefixCls}-minus`,
                onMouseDown: longPressPlus ? this.handleMinus : this.handleOnceMinus,
                onTouchStart: this.handleOnceMinus,
            };
            return this.wrapperInnerSpanButton(React.createElement("div", null,
                React.createElement("div", Object.assign({}, plusIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_up" })),
                React.createElement("div", Object.assign({}, minIconProps),
                    React.createElement(Icon, { type: "keyboard_arrow_down" }))));
        }
        if (clearButton) {
            let right;
            if (this.suffixWidth && this.isSuffixClick) {
                right = defaultTo(this.suffixWidth, 0);
            }
            return this.wrapperInnerSpanButton(React.createElement(Icon, { type: "close", onClick: this.handleClearButtonClick, onMouseDown: this.handleInnerButtonMouseDown }), {
                className: `${prefixCls}-clear-button`,
                style: { right },
            });
        }
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        const step = this.getProp('step');
        const suffix = this.getSuffix();
        const button = this.getInnerSpanButton();
        return super.getWrapperClassNames({
            [`${prefixCls}-step-suffix`]: button && isValidElement(suffix) && step,
            ...args,
        });
    }
    handleEnterDown(e) {
        if (this.multiple && this.range && this.text) {
            this.prepareSetValue(this.text);
        }
        super.handleEnterDown(e);
        if (this.multiple && this.range) {
            this.setRangeTarget(0);
            this.beginRange();
        }
    }
    handleKeyDown(e) {
        if (!this.disabled && !this.readOnly && this.keyboard) {
            switch (e.keyCode) {
                case KeyCode.UP:
                    this.handleKeyDownUp(e);
                    break;
                case KeyCode.DOWN:
                    this.handleKeyDownDown(e);
                    break;
                default:
            }
        }
        super.handleKeyDown(e);
    }
    handleKeyDownUp(value) {
        this.afterStep(value);
    }
    handleKeyDownDown(value) {
        this.afterStep(value);
    }
    handlePlus(value) {
        this.afterStep(value);
    }
    handleMinus(value) {
        this.afterStep(value);
    }
    handleOncePlus() {
        this.step(true);
    }
    handleOnceMinus() {
        this.step(false);
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'nonStrictStep',
            'formatter',
            'formatterOptions',
            'longPressPlus',
            'precision',
            'numberGrouping',
            'maxLength',
            'minLength',
            'keyboard',
            'stringMode',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        delete otherProps.maxLength;
        otherProps.max = this.max;
        otherProps.min = this.min;
        return otherProps;
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            max: props.max,
            min: props.min,
            precision: props.precision,
            nonStrictStep: props.nonStrictStep,
        };
    }
    stepGenerator(isPlus) {
        const { min, max, nonStrictStep, strict } = this;
        const options = {
            strict,
        };
        const step = defaultTo(this.getProp('step'), 1);
        const value = this.getCurrentInputValue();
        const currentValue = getCurrentValidValue(String(value), options);
        return (function* (newValue) {
            while (true) {
                const nearStep = nonStrictStep === false ? getNearStepValues(newValue, step, min, max, options) : undefined;
                if (nonStrictStep === false && nearStep) {
                    switch (nearStep.length) {
                        case 1:
                            newValue = nearStep[0];
                            break;
                        case 2:
                            newValue = nearStep[isPlus ? 1 : 0];
                            break;
                        default:
                    }
                }
                else {
                    const nextValue = isPlus ? math.plus(newValue, step, options) : math.minus(newValue, step, options);
                    if (!isEmpty(min) && math.lt(nextValue, min)) {
                        newValue = min;
                    }
                    else if (!isEmpty(max) && math.gt(nextValue, max)) {
                        const nearMaxStep = nonStrictStep === false ? getNearStepValues(max, step, min, max, options) : undefined;
                        if (nonStrictStep === false && nearMaxStep) {
                            newValue = nearMaxStep[0];
                        }
                        else {
                            newValue = max;
                        }
                    }
                    else {
                        newValue = nextValue;
                    }
                }
                yield newValue;
            }
        })(currentValue);
    }
    getCurrentInputValue() {
        let value;
        if (!isEmpty(this.text)) {
            value = this.text;
        }
        else if (this.range && !isNil(this.rangeTarget) && this.rangeValue) {
            value = this.rangeValue[this.rangeTarget];
        }
        else {
            value = this.getValue();
        }
        return value;
    }
    step(isPlus) {
        this.afterStep(this.stepGenerator(isPlus).next().value);
    }
    afterStep(newValue) {
        if (this.multiple) {
            this.setText(String(newValue));
        }
        else {
            this.prepareSetValue(newValue);
        }
    }
    prepareSetValue(value) {
        if (isNaN(value) || isEmpty(value)) {
            super.prepareSetValue(null);
        }
        else {
            super.prepareSetValue(parseNumber(value, this.getProp('precision'), this.strict));
        }
    }
    getProcessValueOptions() {
        const options = super.getProcessValueOptions();
        options.isNumber = true;
        return options;
    }
    restrictInput(value) {
        if (value) {
            value = value.replace('。', '.');
            let restrict = '0-9';
            if (this.allowDecimal) {
                restrict += '.';
            }
            else if (value.indexOf('.') !== -1) {
                let boforeValue = this.getCurrentInputValue();
                boforeValue = !isNil(boforeValue) ? String(boforeValue) : '';
                if (boforeValue.indexOf('.') === -1) {
                    return boforeValue;
                }
                value = String(this.stepGenerator(false).next().value);
            }
            const isNegative = this.allowNegative && /^-/.test(value);
            value = super.restrictInput(value.replace(new RegExp(`[^${restrict}]+`, 'g'), ''));
            const values = value.split('.');
            if (values.length > 2) {
                value = `${values.shift()}.${values.join('')}`;
            }
            if (isNegative) {
                value = `-${value}`;
            }
        }
        return value;
    }
    getFormatOptions(value) {
        return getNumberFormatOptions((name) => this.getProp(name), (name) => this.getDisplayProp(name), () => this.getValue(), value, this.lang, this.getContextConfig);
    }
    getFormatter() {
        return this.getProp('formatter') || getNumberFormatter(this.getContextConfig);
    }
    processText(value) {
        const fixedValue = math.fix(new BigNumber(String(value)));
        const formatOptions = this.getFormatOptions(fixedValue);
        return this.getFormatter()(value, formatOptions.lang, formatOptions.options);
    }
    renderLengthInfo() {
        return undefined;
    }
    compare(oldValue, newValue) {
        return math.eq(oldValue, newValue);
    }
    isValidInput(input) {
        return !input.endsWith('.');
    }
}
NumberField.displayName = 'NumberField';
NumberField.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'input-number',
    longPressPlus: true,
    max: Infinity,
    min: -Infinity,
};
NumberField.format = defaultFormatNumber;
/**
 * @deprecated
 */
NumberField.bigNumberFormat = defaultFormatNumber;
__decorate([
    computed
], NumberField.prototype, "lang", null);
__decorate([
    computed
], NumberField.prototype, "allowDecimal", null);
__decorate([
    computed
], NumberField.prototype, "nonStrictStep", null);
__decorate([
    computed
], NumberField.prototype, "allowNegative", null);
__decorate([
    computed
], NumberField.prototype, "min", null);
__decorate([
    computed
], NumberField.prototype, "max", null);
__decorate([
    computed
], NumberField.prototype, "value", null);
__decorate([
    autobind
], NumberField.prototype, "savePlusRef", null);
__decorate([
    autobind
], NumberField.prototype, "saveMinusRef", null);
__decorate([
    action
], NumberField.prototype, "handleEnterDown", null);
__decorate([
    autobind
], NumberField.prototype, "handleKeyDown", null);
__decorate([
    keepRunning(stepUpKeyboardProps)
], NumberField.prototype, "handleKeyDownUp", null);
__decorate([
    keepRunning(stepDownKeyboardProps)
], NumberField.prototype, "handleKeyDownDown", null);
__decorate([
    keepRunning(stepUpMouseProps)
], NumberField.prototype, "handlePlus", null);
__decorate([
    keepRunning(stepDownMouseProps)
], NumberField.prototype, "handleMinus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOncePlus", null);
__decorate([
    autobind
], NumberField.prototype, "handleOnceMinus", null);
__decorate([
    autobind
], NumberField.prototype, "compare", null);
let ObserverNumberField = class ObserverNumberField extends NumberField {
};
ObserverNumberField.defaultProps = NumberField.defaultProps;
ObserverNumberField.format = defaultFormatNumber;
/**
 * @deprecated
 */
ObserverNumberField.bigNumberFormat = defaultFormatNumber;
ObserverNumberField = __decorate([
    observer
], ObserverNumberField);
export default ObserverNumberField;
